import React, { useEffect, useState } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import { IconButton } from '@material-ui/core';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import firebase from '../../database/firebase';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const useStyles = makeStyles((theme) => ({
    outerContainer: {
        padding: '0px 10px',
    },
    container: {
        width: '90%',
        maxWidth: '800px',
        margin: 'auto',
        marginTop: 16,
        marginBottom: 16,
        padding: '16px 20px',
        borderRadius: 14,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.08)',
        color: 'white',
    },
    topRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    label: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.5)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        fontWeight: 'bold',
        marginBottom: 0,
    },
    codeContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    code: {
        fontSize: 20,
        fontWeight: 'bold',
        letterSpacing: 4,
        fontFamily: 'monospace',
        marginBottom: 0,
    },
    copyBtn: {
        color: 'rgba(255,255,255,0.5)',
        padding: 6,
        '&:hover': {
            color: 'white',
        }
    },
    referralToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        cursor: 'pointer',
        padding: '4px 10px',
        borderRadius: 20,
        background: 'rgba(255,255,255,0.08)',
        transition: 'background 0.2s',
        '&:hover': {
            background: 'rgba(255,255,255,0.14)',
        }
    },
    referralCount: {
        fontSize: 13,
        fontWeight: 'bold',
        marginBottom: 0,
    },
    referralList: {
        marginTop: 12,
        paddingTop: 12,
        borderTop: '1px solid rgba(255,255,255,0.08)',
    },
    referralItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 0',
    },
    referralName: {
        fontSize: 13,
        fontWeight: 'bold',
        marginBottom: 0,
    },
    referralEmail: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.5)',
        marginBottom: 0,
    },
}));

export default function ReferralSection({ user }) {
    const classes = useStyles();
    const [referrals, setReferrals] = useState([]);
    const [snackOpen, setSnackOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!user || !user.uid) return;
        const unsub = firebase.db.collection("referrals")
            .where("referrerUid", "==", user.uid)
            .onSnapshot(snapshot => {
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setReferrals(data);
            });
        return unsub;
    }, [user?.uid]);

    const handleCopy = () => {
        navigator.clipboard.writeText(user.referralCode || '');
        setSnackOpen(true);
    };

    if (!user || !user.referralCode) return null;

    return (
        <div className={classes.outerContainer}>
            <div className={classes.container}>
                <div className={classes.topRow}>
                    <div>
                        <p className={classes.label}>Tu codigo de referido</p>
                        <div className={classes.codeContainer}>
                            <span className={classes.code}>{user.referralCode}</span>
                            <IconButton className={classes.copyBtn} size="small" onClick={handleCopy}>
                                <ContentCopyRoundedIcon style={{fontSize: 16}}/>
                            </IconButton>
                        </div>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        {referrals.length > 0 && (
                            <div className={classes.referralToggle} onClick={() => setExpanded(!expanded)}>
                                <p className={classes.referralCount}>{referrals.length} referido{referrals.length !== 1 ? 's' : ''}</p>
                                {expanded
                                    ? <ExpandLessRoundedIcon style={{fontSize: 18}}/>
                                    : <ExpandMoreRoundedIcon style={{fontSize: 18}}/>
                                }
                            </div>
                        )}
                        {referrals.length === 0 && (
                            <p className={classes.referralCount} style={{opacity: 0.5}}>0 referidos</p>
                        )}
                    </div>
                </div>

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <div className={classes.referralList}>
                        {referrals.map((r) => (
                            <div key={r.id} className={classes.referralItem}>
                                <p className={classes.referralName}>{r.referredNickname}</p>
                                <p className={classes.referralEmail}>{r.referredEmail}</p>
                            </div>
                        ))}
                    </div>
                </Collapse>

                <Snackbar open={snackOpen} autoHideDuration={3000} onClose={() => setSnackOpen(false)}>
                    <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: '100%' }}>
                        Codigo copiado
                    </Alert>
                </Snackbar>
            </div>
        </div>
    );
}
